import type { ProductListQuery } from '@/api/handlers/resources/products/requests';

/**
 * Centralised TanStack Query keys for the Products resource. Sharing the keys
 * here keeps mutations and queries from drifting out of step when one is
 * invalidated and the other is read.
 *
 * The list key folds the full filter + pagination shape in, so any change to
 * `page` / `search` / `categoryId` / … is a distinct cache entry — switching a
 * filter re-fetches rather than reading stale rows for the wrong query.
 */
export const ProductQueryKeys = {
    all: ['products'] as const,
    lists: () => [...ProductQueryKeys.all, 'list'] as const,
    list: (query: ProductListQuery) =>
        [...ProductQueryKeys.lists(), query] as const,
    details: () => [...ProductQueryKeys.all, 'detail'] as const,
    /**
     * One product's detail, under the branch whose stock it counts
     * (bos-app#119). Without a branch the key is the prefix of every branch's,
     * so invalidating or removing it reaches the product under every branch,
     * which is what an edit or a delete needs.
     */
    detail: (id: string, branchId: string | null = null) =>
        branchId === null
            ? ([...ProductQueryKeys.details(), id] as const)
            : ([...ProductQueryKeys.details(), id, branchId] as const),
};
